/**
 * Stories Store (Pinia)
 * Global state management for stories feature
 * Ported from Flutter: status_provider.dart
 */

import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { StoriesApi } from "@/services/storiesApi";
import { chatState } from "@/stores/chat";

/**
 * Get current user ID from JWT token
 * Fallback to chatState.userId if token parsing fails
 */
function getCurrentUserId() {
  try {
    const token = localStorage.getItem("access_token");
    if (token) {
      const payload = JSON.parse(atob(token.split(".")[1]));
      return payload.sub || payload.user_id || payload.id || "";
    }
  } catch (_) {}
  // Fallback to chatState
  return chatState.userId || "";
}

/**
 * Get current user name from localStorage or chatState
 */
function getCurrentUserName() {
  // Try chatState first
  if (chatState.userName) return chatState.userName;

  // Try to get from JWT token
  try {
    const token = localStorage.getItem("access_token");
    if (token) {
      const payload = JSON.parse(atob(token.split(".")[1]));
      return payload.name || payload.user_name || "";
    }
  } catch (_) {}

  return "";
}

/**
 * Get current user photo from chatState
 */
function getCurrentUserPhoto() {
  return chatState.userPhotoUrl || null;
}

export const useStoriesStore = defineStore("stories", () => {
  // State
  const statusGroups = ref([]);
  const isLoading = ref(false);
  const error = ref(null);

  // Getters
  const hasStatuses = computed(() => statusGroups.value.length > 0);

  /**
   * Fetch all statuses from API
   */
  async function fetchStatuses() {
    isLoading.value = true;
    error.value = null;

    try {
      const data = await StoriesApi.getStatuses();
      statusGroups.value = normalizeStatusGroups(data);
    } catch (e) {
      error.value = e.message || "Failed to fetch statuses";
      statusGroups.value = [];
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * Delete a status and update local state
   */
  async function deleteStatus(statusId) {
    try {
      await StoriesApi.deleteStatus(statusId);

      // Remove status from local list
      statusGroups.value = statusGroups.value
        .map((group) => ({
          ...group,
          statuses: group.statuses.filter((s) => s.id !== statusId),
        }))
        .filter((group) => group.statuses.length > 0);

      return true;
    } catch (e) {
      error.value = e.message || "Failed to delete status";
      return false;
    }
  }

  /**
   * Toggle reaction on a status
   * Matches Flutter: StatusProvider.toggleReaction()
   * Returns: { success: boolean, message?: string }
   */
  async function toggleReaction(statusId) {
    try {
      console.log("[StoriesStore] toggleReaction - statusId:", statusId);

      const response = await StoriesApi.toggleReaction(statusId);
      console.log("[StoriesStore] toggleReaction - API response:", response);

      // Update local status (matching Flutter's approach)
      for (let i = 0; i < statusGroups.value.length; i++) {
        const group = statusGroups.value[i];
        const statusIndex = group.statuses.findIndex((s) => s.id === statusId);

        if (statusIndex !== -1) {
          // Create new array to ensure reactivity
          const updatedStatuses = [...group.statuses];
          updatedStatuses[statusIndex] = {
            ...updatedStatuses[statusIndex],
            userHasReacted: response.userHasReacted,
            reactionCount: response.reactionCount,
          };

          // Update the group
          statusGroups.value = [
            ...statusGroups.value.slice(0, i),
            { ...group, statuses: updatedStatuses },
            ...statusGroups.value.slice(i + 1),
          ];

          console.log(
            "[StoriesStore] toggleReaction - updated status:",
            updatedStatuses[statusIndex]
          );
          break;
        }
      }

      return { success: true };
    } catch (e) {
      console.error("[StoriesStore] toggleReaction error:", e);
      error.value = e.message || "Failed to toggle reaction";
      return { success: false, message: e.message };
    }
  }

  /**
   * Mark status as viewed and update local state
   */
  function markAsViewed(statusId) {
    // Find and update the status locally
    let found = false;
    statusGroups.value = statusGroups.value.map((group) => {
      const statusIndex = group.statuses.findIndex((s) => s.id === statusId);
      if (statusIndex === -1) return group;

      // Only send API request if not already viewed
      if (!group.statuses[statusIndex].userHasViewed && !found) {
        found = true;
        StoriesApi.markAsViewed(statusId); // Fire and forget
      }

      const updatedStatuses = group.statuses.map((s) =>
        s.id === statusId ? { ...s, userHasViewed: true } : s
      );

      // Update hasUnseen flag
      const hasUnseen = updatedStatuses.some((s) => !s.userHasViewed);

      return {
        ...group,
        statuses: updatedStatuses,
        hasUnseen,
      };
    });
  }

  /**
   * Normalize API response to consistent format
   * Handles two response formats:
   * 1. Grouped by user: [{ user_id, user: {...}, statuses: [...] }, ...]
   * 2. Flat list: [{ id, user_id, user: {...}, type, ... }, ...]
   */
  function normalizeStatusGroups(data) {
    if (!Array.isArray(data)) return [];

    // Check if data is already grouped (has 'statuses' array) or flat list
    const isGrouped = data.length > 0 && Array.isArray(data[0]?.statuses);

    if (isGrouped) {
      // Already grouped format
      return data.map((group) => ({
        userId: group.user_id || group.userId || group.user?.id,
        user: normalizeUser(group.user),
        hasUnseen: group.has_unseen ?? group.hasUnseen ?? true,
        statuses: (group.statuses || []).map(normalizeStatus),
      }));
    }

    // Flat list format - group by user_id
    const groupsMap = new Map();

    data.forEach((status) => {
      const userId = status.user_id || status.userId;
      if (!groupsMap.has(userId)) {
        groupsMap.set(userId, {
          userId,
          user: normalizeUser(status.user, userId),
          hasUnseen: true,
          statuses: [],
        });
      }
      groupsMap.get(userId).statuses.push(normalizeStatus(status));
    });

    // Convert map to array and calculate hasUnseen
    return Array.from(groupsMap.values()).map((group) => ({
      ...group,
      hasUnseen: group.statuses.some((s) => !s.userHasViewed),
    }));
  }

  function normalizeUser(user, userId = null) {
    // If no user object provided, try to use current user info
    if (!user) {
      // Check if this is the current user's status
      const currentUserId = getCurrentUserId();
      const statusUserId = userId ? String(userId) : null;

      if (
        currentUserId &&
        statusUserId &&
        String(currentUserId) === statusUserId
      ) {
        // This is the current user's status - use their info
        const userName = getCurrentUserName();
        const userPhoto = getCurrentUserPhoto();

        return {
          id: parseInt(currentUserId) || 0,
          name: userName || null,
          fname: null,
          lname: null,
          image: userPhoto,
          role: chatState.role || null,
          get displayName() {
            return this.name || "Me";
          },
          get displayImage() {
            return this.image || null;
          },
        };
      }

      // Unknown user
      return {
        id: userId || 0,
        name: "Unknown",
        displayName: "Unknown",
        displayImage: null,
      };
    }

    return {
      id: user.id,
      name: user.name,
      fname: user.fname || user.first_name,
      lname: user.lname || user.last_name,
      image: user.image || user.user_img,
      role: user.role,
      // Computed display name
      get displayName() {
        if (this.name) return this.name;
        const first = this.fname || "";
        const last = this.lname || "";
        const combined = `${first} ${last}`.trim();
        return combined || "Unknown";
      },
      // Computed display image
      get displayImage() {
        return this.image || null;
      },
    };
  }

  function normalizeStatus(status) {
    return {
      id: status.id,
      userId: status.user_id || status.userId,
      type: status.type, // 'text', 'image', 'video'
      mediaDeliveryUrl: status.media_delivery_url || status.mediaDeliveryUrl,
      thumbnailDeliveryUrl:
        status.thumbnail_delivery_url || status.thumbnailDeliveryUrl,
      textOverlay: status.text_overlay || status.textOverlay,
      description: status.description,
      statusState: status.status_state || status.statusState,
      publishedAt: status.published_at || status.publishedAt,
      expiresAt: status.expires_at || status.expiresAt,
      createdAt: status.created_at || status.createdAt,
      isExpired: status.is_expired ?? status.isExpired ?? false,
      isActive: status.is_active ?? status.isActive ?? true,
      reactionCount: status.reaction_count ?? status.reactionCount ?? 0,
      viewCount: status.view_count ?? status.viewCount ?? 0,
      userHasReacted: status.user_has_reacted ?? status.userHasReacted ?? false,
      userHasViewed: status.user_has_viewed ?? status.userHasViewed ?? false,
    };
  }

  return {
    // State
    statusGroups,
    isLoading,
    error,
    // Getters
    hasStatuses,
    // Actions
    fetchStatuses,
    deleteStatus,
    toggleReaction,
    markAsViewed,
  };
});

export default useStoriesStore;
